// [S-073a] Precedent upload portal.
//
// Tenant_user / tenant_admin flow: drop (or pick) up to ~500 files; each fires
// the admin upload route. Infer-first: classification (deal type / guild / tier)
// is auto-detected during ingest, then EVERY file enters the human review queue
// to be confirmed before it indexes — nothing auto-indexes (controlled L2 corpus).
//
// Master Rule 1.6 doesn't apply on this surface (L2-only).
//
// Lifecycle polling: once the upload mutation returns a documentId, each row
// mounts a useDocumentStatus hook that polls `GET /tenants/:id/documents/:docId`
// every 2s. The badge follows the lifecycle (Analyzing → In Review → Ready or
// Processing Failed). As a file FINISHES the automated pipeline — reaches the
// review queue (In Review), indexes (Ready), or is a duplicate — it leaves the
// list after a short grace, so the list stays a live view of in-flight work; a
// running tally records what left. `failed` rows are NOT auto-removed (the error
// must stay visible) and replay is platform_admin-only (S-033 + S-073b), so we
// deliberately render no retry control here.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import {
  Button,
  PageHead,
  Pagination,
  Select,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  usePagination,
} from "../components";
import { ReviewQueueBanner } from "./ReviewQueueBanner";
import { AllUploadsPanel } from "./AllUploadsPanel";
import { DriveImportButton } from "./DriveImportButton";
import {
  MAX_BATCH_FILES,
  collectFilesFromDataTransfer,
  describeIntake,
  filterAccepted,
  type IntakeResult,
} from "../data/fileIntake";
import {
  describeUploadError,
  isAbortError,
  isTerminalLifecycle,
  makePrecedentRouteFor,
  useDocumentStatus,
  useUploadFile,
  type DocumentClassification,
  type DocumentLifecycleState,
  type UploadFileResult,
  type ZipUploadResult,
} from "../data/uploadQuery";
import { useTenantsList } from "../data/tenantsQuery";

// `queued | uploading` are pre-server states. After upload returns,
// the row tracks the server's lifecycleState (the union from the data
// hook). `duplicate` and `error` are mutation-result states the poll
// hook never sees. Keeping them all in one local discriminator keeps
// the row renderer flat.
type StagedStatus =
  | "queued"
  | "uploading"
  | "duplicate"
  | "error"
  | "canceled" // [S-268] user aborted the in-flight upload
  | "zip" // [S-267] a .zip archive that fanned out into per-file results
  | DocumentLifecycleState;

type Staged = {
  id: string; // local correlation id (file.name + size + lastModified)
  file: File;
  status: StagedStatus;
  documentId?: string;
  message?: string;
  zip?: ZipUploadResult; // [S-267] per-file summary when status === "zip"
};

export function Upload(): JSX.Element {
  const session = useSession();
  // Prefer session.activeTenantId — it's what the request headers carry
  // (x-csai-active-tenant), so the upload URL :id, the auth scope, AND the
  // All-Uploads "pull" all resolve to the SAME tenant. session.studioId lags a
  // tenant switch (its derivation waits on the membership lookup), so keying off
  // it made uploads land in / be authorised against the login tenant while the
  // headers + list followed the switched one — "uploaded but nothing in All
  // Uploads." Mirrors Home.tsx. Falls back to the login studio, then the
  // platform_admin rail picker below.
  const sessionTenantId =
    session?.activeTenantId ??
    (session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : null);
  // A principal with no home studio (studioId null — platform staff) doesn't
  // own an organization; they pick one to upload on behalf of via the rail
  // picker below. A studio-scoped user is always scoped to their own studio.
  // This is a SCOPE decision, not an authz one — upload itself is
  // ingestion:submit, held by every member, and is never gated here.
  const needsTenantScope = session?.studioId == null;
  // [S-265] Review-queue actions gate on review:moderate (held by
  // tenant_admin / platform_admin and any custom role granted it). Upload
  // itself stays open to every member; only the queue is capability-gated.
  const canReview = sessionCan(session, "review:moderate");
  const [pickedTenantId, setPickedTenantId] = useState<string | null>(null);
  const tenantId: string | null = sessionTenantId ?? pickedTenantId;

  // Two tabs on one page: "Upload" (drop zone + this session's live queue) and
  // "All uploads" (the durable, backend-backed status board). The active tab is
  // reflected in ?tab= so deep-links from elsewhere (and the affirmation/zip
  // "view status" links below) can land straight on the status board.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "all" ? "all" : "upload";
  const setTab = useCallback(
    (next: string): void => {
      setSearchParams(next === "all" ? { tab: "all" } : {}, { replace: false });
    },
    [setSearchParams]
  );
  // "All uploads" scope: platform_admin has no studio, so pass the picked
  // tenant; tenant users leave it undefined and the server forces own scope.
  // The "All uploads" pull MUST scope to the SAME tenant we upload into, or an
  // upload vanishes from the list. Mirror the upload `tenantId` exactly (which
  // now follows the active tenant), instead of resolving it independently.
  const allUploadsTenantId = tenantId ?? undefined;
  const needsTenantPick = needsTenantScope && pickedTenantId === null;

  const uploadMutation = useUploadFile();

  const [staged, setStaged] = useState<Staged[]>([]);
  const [isDrag, setIsDrag] = useState(false);

  const totalBytes = useMemo(
    () => staged.reduce((acc, s) => acc + s.file.size, 0),
    [staged]
  );

  const addFiles = useCallback((picked: FileList | File[]): void => {
    const next = Array.from(picked).map<Staged>((f) => ({
      id: `${f.name}:${f.size}:${f.lastModified}`,
      file: f,
      status: "queued",
    }));
    setStaged((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      return [...prev, ...next.filter((s) => !seen.has(s.id))];
    });
  }, []);

  const removeStaged = useCallback((id: string): void => {
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // [S-264] Rows PERSIST after they finish — they no longer silently vanish
  // after a grace period. Bill's feedback: a file that disappears reads as
  // "nothing happened." Each row now stays put showing an honest terminal
  // status (In review / Searchable / Already in corpus / Failed) with a
  // deep-link to where the work continues. "Clear" is the only thing that
  // empties the list.

  // [S-268] One AbortController per in-flight upload, keyed by row id, so a
  // per-row ✕ (or Cancel-all) can abort the request instead of leaving the user
  // stuck watching a large upload with no escape hatch.
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  // Upload a single staged file and let the polling hook take over its
  // lifecycle from there. Classification is automatic on ingest.
  const uploadOne = useCallback(
    async (s: Staged): Promise<void> => {
      if (tenantId === null) return;
      const controller = new AbortController();
      abortControllers.current.set(s.id, controller);
      setStaged((prev) =>
        prev.map((p) => (p.id === s.id ? { ...p, status: "uploading" } : p))
      );
      try {
        const result: UploadFileResult = await uploadMutation.mutateAsync({
          tenantId,
          file: s.file,
          signal: controller.signal,
        });
        setStaged((prev) =>
          prev.map((p) => {
            if (p.id !== s.id) return p;
            // [S-267] A .zip fanned out into per-file results — hold the
            // summary on the row; the individual docs live in the archive.
            if (result.zip === true) {
              return { ...p, status: "zip", zip: result };
            }
            return {
              ...p,
              status: result.duplicate ? "duplicate" : result.lifecycleState,
              documentId: result.documentId,
              message: result.lifecycleState,
            };
          })
        );
      } catch (e) {
        // [S-268] A user-initiated cancel is not a failure — mark it distinctly
        // (offers Retry) so it doesn't read as a broken upload.
        const canceled = isAbortError(e);
        setStaged((prev) =>
          prev.map((p) =>
            p.id === s.id
              ? {
                  ...p,
                  status: canceled ? "canceled" : "error",
                  message: describeUploadError(e),
                }
              : p
          )
        );
      } finally {
        abortControllers.current.delete(s.id);
      }
    },
    [tenantId, uploadMutation]
  );

  // [S-268] Cancel a single row: abort the request if it's uploading, otherwise
  // (queued) just drop it. Re-queueing a canceled row retries it.
  const cancelOne = useCallback((id: string): void => {
    const controller = abortControllers.current.get(id);
    if (controller !== undefined) {
      controller.abort();
      return;
    }
    // Queued (not yet uploading) → remove it so it never starts.
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const retryOne = useCallback((id: string): void => {
    setStaged((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: "queued", message: undefined } : s
      )
    );
  }, []);

  // [S-268] Abort the current upload and clear everything still queued — the
  // escape hatch for a big drop that's mid-flight.
  const cancelAll = useCallback((): void => {
    for (const c of abortControllers.current.values()) c.abort();
    setStaged((prev) => prev.filter((s) => s.status !== "queued"));
  }, []);

  // Auto-index: as soon as a file is staged (and an organization is known), upload it
  // — no button. Processed one at a time (the ref gates concurrency) so a big
  // drop doesn't fire dozens of concurrent multipart uploads; each file's
  // progress then advances independently via its polling row. platform_admin
  // who drops files before picking an organization: the queued files start the moment
  // an organization is selected.
  const uploadingRef = useRef(false);
  useEffect(() => {
    if (tenantId === null || uploadingRef.current) return;
    const next = staged.find((s) => s.status === "queued");
    if (next === undefined) return;
    uploadingRef.current = true;
    void uploadOne(next).finally(() => {
      uploadingRef.current = false;
    });
  }, [staged, tenantId, uploadOne]);

  const reset = useCallback((): void => {
    setStaged([]);
  }, []);

  // Polling rows ping us when the server reports a new lifecycleState so the
  // parent's `staged` array stays the source of truth (for the affirmation
  // counts + queued gating). We only update if the status actually changed.
  const onStatusChange = useCallback(
    (id: string, next: DocumentLifecycleState): void => {
      setStaged((prev) =>
        prev.map((p) => (p.id === id && p.status !== next ? { ...p, status: next } : p))
      );
    },
    []
  );

  const queuedCount = staged.filter((s) => s.status === "queued").length;
  const inFlight = uploadMutation.isPending;

  // [S-264] Affirmation counts, derived live from the persistent rows. These
  // drive the "your uploads are in review" banner so the user gets an explicit
  // confirmation instead of a silent disappearance.
  const inReviewCount = staged.filter(
    (s) => s.status === "stage2_review" || s.status === "stage3_human"
  ).length;
  const indexedCount = staged.filter((s) => s.status === "indexed").length;
  const duplicateCount = staged.filter((s) => s.status === "duplicate").length;
  const zipCount = staged.filter((s) => s.status === "zip").length;
  const settledCount =
    inReviewCount + indexedCount + duplicateCount + zipCount;

  return (
    <>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot stud" /> Precedent · upload
          </>
        }
        title={
          <>
            Add deals to the <em>archive.</em>
          </>
        }
        sub="Bring closed deals into your precedent. Deal type, guild, and tier are auto-detected on ingest — just drop the files. They stay in your organization."
      />

      {/* A principal with no home studio picks one here — it governs BOTH tabs
          (upload target + the "All uploads" status scope). */}
      {needsTenantScope && (
        <PlatformAdminTenantPicker
          value={pickedTenantId}
          onChange={setPickedTenantId}
        />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList ariaLabel="Upload views" className="upl__tabs">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="all">All uploads</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          {/* [S-265] Surfaces the pending-review count for reviewers, wherever
              they are, and routes them to the queue that makes uploads
              searchable. */}
          {canReview && <ReviewQueueBanner />}

          <div className="upl__split">
            <div className="upl__main">
              <Dropzone
                isDrag={isDrag}
                setIsDrag={setIsDrag}
                addFiles={addFiles}
                tenantId={tenantId}
              />
              {/* [S-264] Explicit post-upload affirmation — the confirmation
                  Bill found missing. Appears as soon as anything finishes
                  uploading. */}
              {settledCount > 0 && (
                <UploadAffirmation
                  inReviewCount={inReviewCount}
                  indexedCount={indexedCount}
                  duplicateCount={duplicateCount}
                  zipCount={zipCount}
                  canReview={canReview}
                />
              )}
              <Queue
                staged={staged}
                totalBytes={totalBytes}
                tenantId={tenantId}
                onRemove={removeStaged}
                onCancel={cancelOne}
                onRetry={retryOne}
                onStatusChange={onStatusChange}
              />
            </div>

            <aside className="upl__rail">
              <div className="upl__tag" data-testid="upl-tag-panel">
                <div className="upl__tag__lbl">How indexing works</div>
                <div className="upl__tag__note">
                  Deal type, guild, tier, and the clause structure are{" "}
                  <b>auto-detected on ingest</b>. Files then enter the{" "}
                  <b>human review queue</b> where any classification can be
                  corrected per file before they index.
                </div>
              </div>

              <div className="upl__actions">
                {/* No "index" button — files auto-upload on drop. This is just a
                    status line + a clear-list affordance. Finished rows stay in
                    the list (S-264) until you Clear them. */}
                <span className="upl__autostatus" data-testid="upl-autostatus">
                  {staged.length === 0
                    ? "Drop files to process"
                    : tenantId === null
                      ? "Pick an organization to begin →"
                      : inFlight || queuedCount > 0
                        ? `Processing… (${queuedCount} queued)`
                        : settledCount > 0
                          ? "All files processed — see status at left."
                          : "Processing…"}
                </span>
                {/* [S-268] Escape hatch while a big drop is mid-flight — aborts
                    the current upload and drops everything still queued. */}
                {(inFlight || queuedCount > 0) && (
                  <Button
                    variant="secondary"
                    className="upl__cancel"
                    onClick={cancelAll}
                    data-testid="upl-cancel-all"
                  >
                    Cancel {queuedCount > 0 ? `(${queuedCount} queued)` : ""}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="upl__cancel"
                  disabled={inFlight || staged.length === 0}
                  onClick={reset}
                >
                  Clear
                </Button>
              </div>
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="all">
          {/* The durable, backend-backed status board — every doc the studio has
              uploaded, across all lifecycle states. This is where the standalone
              /archive page's content now lives (operational, not a peer of the
              readable Precedent library). */}
          <AllUploadsPanel
            scopeTenantId={allUploadsTenantId}
            needsPick={needsTenantPick}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

function Dropzone({
  isDrag,
  setIsDrag,
  addFiles,
  tenantId,
}: {
  isDrag: boolean;
  setIsDrag: (v: boolean) => void;
  addFiles: (files: FileList | File[]) => void;
  tenantId: string | null;
}): JSX.Element {
  // [S-266] Honest intake summary — how many files were added, and how many
  // were skipped (unsupported) or dropped by the batch cap. Never silent.
  const [notice, setNotice] = useState<string | null>(null);

  // [S-266] webkitdirectory is a non-standard attribute React's types don't
  // know about — set it imperatively on mount rather than casting props.
  const folderInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const intake = useCallback(
    (result: IntakeResult): void => {
      if (result.files.length > 0) addFiles(result.files);
      setNotice(describeIntake(result));
    },
    [addFiles]
  );

  return (
    <div
      className={isDrag ? "upl__drop is-drag" : "upl__drop"}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDrag(true);
      }}
      onDragLeave={() => setIsDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDrag(false);
        // [S-266] Walk any dropped folders recursively. Capture the entries
        // synchronously inside collectFilesFromDataTransfer before awaiting.
        void collectFilesFromDataTransfer(e.dataTransfer).then(intake);
      }}
      data-testid="upl-drop"
    >
      <div className="upl__drop__title">
        Drop deals to <em>archive.</em>
      </div>
      <div className="upl__drop__hint">
        DOCX, PDF, TXT or MD — drop a folder or a .zip of them. Up to{" "}
        {MAX_BATCH_FILES} files, max 50MB each.
      </div>
      {/* Every import source in one row: local files, a folder, and the
          connector sources. Drive is live; SharePoint routes to the Settings
          connector panel (its OAuth flow isn't built yet — honest, not faked). */}
      <div className="upl__drop__sources">
        <label className="upl__src upl__src--primary">
          <FileIcon />
          Browse files
          <input
            type="file"
            multiple
            accept=".docx,.pdf,.txt,.md,.zip,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,application/zip,application/x-zip-compressed"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files !== null && e.target.files.length > 0) {
                intake(filterAccepted(Array.from(e.target.files)));
              }
              // Reset so re-selecting the same file fires onChange again
              e.target.value = "";
            }}
            data-testid="upl-input"
          />
        </label>
        <label className="upl__src upl__src--ghost">
          <FolderIcon />
          Browse folder
          <input
            ref={folderInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files !== null && e.target.files.length > 0) {
                intake(filterAccepted(Array.from(e.target.files)));
              }
              e.target.value = "";
            }}
            data-testid="upl-folder-input"
          />
        </label>
        {tenantId !== null && (
          <DriveImportButton tenantId={tenantId} target="deals" appearance="source" />
        )}
        <Link
          to="/settings#connectors"
          className="upl__src upl__src--ghost"
          data-testid="upl-src-sharepoint"
        >
          <span className="upl__src__mark upl__src__mark--sharepoint" aria-hidden="true">
            S
          </span>
          SharePoint
        </Link>
      </div>
      {notice !== null && (
        <div className="upl__drop__notice" role="status" data-testid="upl-intake-notice">
          {notice}
        </div>
      )}
      <div className="upl__drop__formats">
        DOCX · PDF · TXT · MD · ZIP · max 50MB / file
      </div>
    </div>
  );
}

function Queue({
  staged,
  totalBytes,
  tenantId,
  onRemove,
  onCancel,
  onRetry,
  onStatusChange,
}: {
  staged: Staged[];
  totalBytes: number;
  tenantId: string | null;
  onRemove: (id: string) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onStatusChange: (id: string, next: DocumentLifecycleState) => void;
}): JSX.Element {
  const pager = usePagination(staged, 25);
  return (
    <div className="upl__queue" data-testid="upl-queue">
      <div className="upl__queue__lbl">
        <span>
          {staged.length} file{staged.length === 1 ? "" : "s"} staged
        </span>
        <span className="total">{formatBytes(totalBytes)} total</span>
      </div>
      {staged.length === 0 ? (
        <div className="upl__empty">No files staged yet.</div>
      ) : (
        pager.pageItems.map((s) => (
          <div className="upl__queue__row" key={s.id} data-testid={`upl-row-${s.id}`}>
            <div className="upl__queue__icon">{extOf(s.file.name)}</div>
            <div>
              <div className="upl__queue__name" title={s.file.name}>
                {s.file.name}
              </div>
              <div className="upl__queue__meta">
                {pagesGuess(s.file)} ·{" "}
                {new Date(s.file.lastModified).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
            <div className="upl__queue__sz">{formatBytes(s.file.size)}</div>
            <RowStatus
              staged={s}
              tenantId={tenantId}
              onRemove={onRemove}
              onCancel={onCancel}
              onRetry={onRetry}
              onStatusChange={onStatusChange}
            />
          </div>
        ))
      )}
      <Pagination
        page={pager.page}
        pageCount={pager.pageCount}
        onPageChange={pager.setPage}
        total={pager.total}
        rangeStart={pager.rangeStart}
        rangeEnd={pager.rangeEnd}
        itemLabel="file"
      />
    </div>
  );
}

// Row status renderer. For queued / uploading / duplicate / error
// rows we render a static badge (or the remove control) — the upload
// hasn't touched the server yet, or is in a terminal client-side
// state. For lifecycle states we mount <PollingStatus> which owns the
// status query.
function RowStatus({
  staged,
  tenantId,
  onRemove,
  onCancel,
  onRetry,
  onStatusChange,
}: {
  staged: Staged;
  tenantId: string | null;
  onRemove: (id: string) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onStatusChange: (id: string, next: DocumentLifecycleState) => void;
}): JSX.Element {
  switch (staged.status) {
    case "queued":
      return (
        <Button
          variant="bare"
          className="upl__queue__status upl__queue__status--queued"
          onClick={() => onRemove(staged.id)}
          aria-label={`Remove ${staged.file.name}`}
        >
          ✕
        </Button>
      );
    case "uploading":
      // [S-268] Cancel the in-flight upload (aborts the request).
      return (
        <span className="upl__queue__uploading">
          <span className="upl__queue__status upl__queue__status--up">
            Uploading…
          </span>
          <Button
            variant="bare"
            className="upl__queue__cancel"
            onClick={() => onCancel(staged.id)}
            aria-label={`Cancel ${staged.file.name}`}
            data-testid={`upl-cancel-${staged.id}`}
          >
            ✕
          </Button>
        </span>
      );
    case "canceled":
      // [S-268] Not a failure — offer a one-click retry (re-queues the row).
      return (
        <span className="upl__queue__indexed" data-testid={`upl-status-${staged.id}`}>
          <span className="upl__queue__status upl__queue__status--dup">
            Canceled
          </span>
          <Button
            variant="bare"
            className="upl__queue__cir"
            onClick={() => onRetry(staged.id)}
            data-testid={`upl-retry-${staged.id}`}
          >
            Retry →
          </Button>
        </span>
      );
    case "duplicate":
      return (
        <span
          className="upl__queue__status upl__queue__status--dup"
          data-testid={`upl-status-${staged.id}`}
        >
          Already processed
        </span>
      );
    case "error":
      return (
        <span
          className="upl__queue__status upl__queue__status--err"
          title={staged.message ?? "error"}
          data-testid={`upl-status-${staged.id}`}
        >
          {staged.message ?? "Failed"}
        </span>
      );
    case "zip":
      return <ZipRowStatus rowId={staged.id} zip={staged.zip} />;
    default:
      // Lifecycle state — server owns the truth; mount the polling hook.
      return (
        <PollingStatus
          rowId={staged.id}
          tenantId={tenantId}
          documentId={staged.documentId ?? null}
          initialState={staged.status}
          onStatusChange={onStatusChange}
        />
      );
  }
}

// Lifecycle-aware status pill. Polls the document status endpoint and
// renders a badge whose label + colour reflect the current
// lifecycleState. Two terminal states get richer treatment:
//   - `indexed` → "Ready" + a CIR deep link into the precedents panel
//   - `failed`  → "Processing Failed" + an explainer that recovery is
//                 platform-admin-only (S-073b). NO retry button is
//                 rendered — surfacing one would either lie to a
//                 tenant_user audience or require widening the role
//                 gate, which we will not do.
function PollingStatus({
  rowId,
  tenantId,
  documentId,
  initialState,
  onStatusChange,
}: {
  rowId: string;
  tenantId: string | null;
  documentId: string | null;
  initialState: DocumentLifecycleState;
  onStatusChange: (id: string, next: DocumentLifecycleState) => void;
}): JSX.Element {
  // [S-264] Rows persist now, so stop polling once the doc reaches the review
  // queue — the uploader's part is done; a reviewer advances it from there.
  const { data } = useDocumentStatus(tenantId, documentId, { stopAtReview: true });
  const lifecycle: DocumentLifecycleState = data?.lifecycleState ?? initialState;

  // Sync the latest server state back up to the parent so the rest of
  // the UI (and any aggregate counters) sees the same truth.
  useEffect(() => {
    if (data?.lifecycleState !== undefined && data.lifecycleState !== initialState) {
      onStatusChange(rowId, data.lifecycleState);
    }
  }, [data?.lifecycleState, initialState, onStatusChange, rowId]);

  return (
    <div className="upl__queue__statuscol">
      <ClassificationChips classification={data?.classification} />
      <LifecycleBadge
        lifecycle={lifecycle}
        tenantId={tenantId}
        documentId={documentId}
        rowId={rowId}
        failure={data?.failure ?? null}
      />
    </div>
  );
}

// S-216 — per-file LLM classification (deal type / guild / tier / visibility),
// shown once Stage 2 has run. Empty until then.
function prettyTag(v: string): string {
  return v.replace(/_/g, " ");
}
function ClassificationChips({
  classification,
}: {
  classification?: DocumentClassification;
}): JSX.Element | null {
  if (classification === undefined) return null;
  const items = [
    classification.dealType,
    classification.guild,
    classification.talentTier,
    classification.visibility,
  ].filter((v): v is string => typeof v === "string" && v.length > 0);
  if (items.length === 0) return null;
  return (
    <div className="upl__queue__tags" data-testid="upl-tags">
      {items.map((t) => (
        <span className="upl__queue__tag" key={t}>
          {prettyTag(t)}
        </span>
      ))}
    </div>
  );
}

// [failure-visibility] Which part of the pipeline gave up, in the user's terms.
// A raw "stage2" means nothing to an uploader deciding whether to retry.
const FAILURE_STAGE_LABELS: Record<string, string> = {
  stage1: "Couldn't read the file",
  stage2: "Couldn't extract the deal terms",
  stage3: "Couldn't prepare it for review",
  hybrid: "Couldn't route to your on-prem model",
};

const LIFECYCLE_LABELS: Record<DocumentLifecycleState, string> = {
  received: "Processing…",
  parsing: "Processing…",
  stage1_complete: "Analyzing…",
  stage2_review: "In Review…",
  stage3_human: "In Review…",
  indexed: "Ready",
  failed: "Processing Failed",
};

const LIFECYCLE_PILL_CLASS: Record<DocumentLifecycleState, string> = {
  received: "upl__queue__status--up",
  parsing: "upl__queue__status--up",
  stage1_complete: "upl__queue__status--analyzing",
  stage2_review: "upl__queue__status--review",
  stage3_human: "upl__queue__status--review",
  indexed: "upl__queue__status--ok",
  failed: "upl__queue__status--err",
};

function LifecycleBadge({
  lifecycle,
  tenantId,
  documentId,
  rowId,
  failure,
}: {
  lifecycle: DocumentLifecycleState;
  tenantId: string | null;
  documentId: string | null;
  rowId: string;
  // [failure-visibility] Why this document failed, when we know it. Null for a
  // document that failed before the reason was recorded — its reason only ever
  // existed in the ingestion container's log.
  failure?: { stage: string; message: string; at: string } | null;
}): JSX.Element {
  const pillClass = `upl__queue__status ${LIFECYCLE_PILL_CLASS[lifecycle]}`;
  const label = LIFECYCLE_LABELS[lifecycle];

  if (lifecycle === "indexed" && tenantId !== null && documentId !== null) {
    return (
      <span className="upl__queue__indexed" data-testid={`upl-status-${rowId}`}>
        <span className={pillClass}>{label}</span>
        <a
          className="upl__queue__cir"
          href={makePrecedentRouteFor(documentId)}
          data-cir-href={`contractstudio://collection/${tenantId}/${documentId}`}
          data-testid={`upl-cir-${rowId}`}
        >
          Open in precedents →
        </a>
      </span>
    );
  }

  if (lifecycle === "failed") {
    return (
      <span className="upl__queue__failed" data-testid={`upl-status-${rowId}`}>
        <span className={pillClass}>{label}</span>
        {/* [failure-visibility] WHAT went wrong and WHERE, above the existing
            next-step hint. Without this the row said "Processing Failed" and
            nothing else — the reason lived only in a log the app can't read, so
            nobody could tell a transient infrastructure kill (worth retrying)
            from a file the parser will never accept. Absent on documents that
            failed before this was recorded; the hint alone still shows. */}
        {failure !== null && failure !== undefined ? (
          <span
            className="upl__queue__failed__reason"
            data-testid={`upl-failure-${rowId}`}
          >
            <strong>{FAILURE_STAGE_LABELS[failure.stage] ?? failure.stage}:</strong>{" "}
            {failure.message}
          </span>
        ) : null}
        <span className="upl__queue__failed__hint">
          A platform admin will review and replay this file.
        </span>
      </span>
    );
  }

  // [S-264] Reached the human review queue — affirm it landed and point the
  // user at where they can track/act on it, instead of leaving a bare pill.
  if (lifecycle === "stage2_review" || lifecycle === "stage3_human") {
    return (
      <span className="upl__queue__indexed" data-testid={`upl-status-${rowId}`}>
        <span className={pillClass}>{label}</span>
        <Link className="upl__queue__cir" to="/upload?tab=all">
          Track status →
        </Link>
      </span>
    );
  }

  // In-flight states use aria-live so screen readers get the lifecycle
  // tick without a focus jump.
  return (
    <span
      className={pillClass}
      aria-live={isTerminalLifecycle(lifecycle) ? undefined : "polite"}
      data-testid={`upl-status-${rowId}`}
    >
      {label}
    </span>
  );
}

// Platform admins don't own an organization; on csai-baf9d the engineer logs
// in as platform_admin and would otherwise be locked out of the upload
// portal. The picker lets them choose any provisioned tenant to upload
// on behalf of. The server enforces the role gate (platform_admin
// already has cross-tenant write powers), so this is a UI affordance,
// not a permission elevation.
function PlatformAdminTenantPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string) => void;
}): JSX.Element {
  const tenantsQuery = useTenantsList();
  const tenants = tenantsQuery.data?.tenants ?? [];
  return (
    <div className="upl__tag" data-testid="upl-tenant-picker">
      <div className="upl__tag__lbl">Upload on behalf of</div>
      <div className="upl__tag__row">
        <div className="k">
          Tenant
          <em>required for platform admins</em>
        </div>
        {tenantsQuery.isLoading ? (
          <div className="upl__tenant-picker__hint"><Spinner size={13} /> Loading tenants…</div>
        ) : tenantsQuery.isError ? (
          <div className="upl__tenant-picker__hint">
            Couldn't load tenants: {tenantsQuery.error?.message ?? "unknown"}
          </div>
        ) : tenants.length === 0 ? (
          <div className="upl__tenant-picker__hint">
            No tenants provisioned yet — visit{" "}
            <Link to="/tenants">Organizations</Link> to create one.
          </div>
        ) : (
          <Select<string>
            label="Organization"
            value={value ?? undefined}
            onValueChange={(v) => v !== undefined && onChange(v)}
            clearable={false}
            placeholder="Pick an organization…"
            options={tenants.map((t) => ({
              value: t.id,
              label: t.studioId,
            }))}
          />
        )}
      </div>
    </div>
  );
}

// [S-264] The post-upload affirmation — an explicit, persistent confirmation
// that files landed and what happens next, with deep-links to the queue
// (reviewers) and the archive (everyone). Replaces the old behavior where rows
// silently disappeared and the only signal was a fading tally.
function UploadAffirmation({
  inReviewCount,
  indexedCount,
  duplicateCount,
  zipCount,
  canReview,
}: {
  inReviewCount: number;
  indexedCount: number;
  duplicateCount: number;
  zipCount: number;
  canReview: boolean;
}): JSX.Element {
  const parts = [
    inReviewCount > 0 ? `${inReviewCount} in review` : null,
    indexedCount > 0 ? `${indexedCount} searchable` : null,
    duplicateCount > 0 ? `${duplicateCount} already in corpus` : null,
    zipCount > 0 ? `${zipCount} archive${zipCount === 1 ? "" : "s"} expanded` : null,
  ].filter((p): p is string => p !== null);
  // A zip almost always adds review items; treat either as "in review".
  const anyReview = inReviewCount > 0 || zipCount > 0;
  return (
    <div className="upl__affirm" data-testid="upl-affirmation" role="status">
      <span className="upl__affirm__check" aria-hidden="true">
        ✓
      </span>
      <div className="upl__affirm__body">
        <div className="upl__affirm__title">Uploaded — {parts.join(" · ")}.</div>
        <div className="upl__affirm__note">
          {anyReview
            ? canReview
              ? "Files enter the human review queue. Approve them there to make them searchable."
              : "Files enter the human review queue. A reviewer will approve them to make them searchable."
            : "Your files are in your uploads."}
        </div>
      </div>
      <div className="upl__affirm__actions">
        {anyReview && canReview && (
          <Link to="/queue" className="btn">
            Open review queue →
          </Link>
        )}
        <Link to="/upload?tab=all" className="btn btn--g">
          All uploads →
        </Link>
      </div>
    </div>
  );
}

// [S-267] Per-row summary for a .zip that fanned out into many documents. The
// individual docs are tracked in the archive, not polled here.
function ZipRowStatus({
  rowId,
  zip,
}: {
  rowId: string;
  zip?: ZipUploadResult;
}): JSX.Element {
  if (zip === undefined) {
    return <span className="upl__queue__status">Extracted</span>;
  }
  const skipped = zip.skipped.length;
  const failed = zip.files.filter((f) => f.error !== undefined).length;
  return (
    <span className="upl__queue__indexed" data-testid={`upl-status-${rowId}`}>
      <span className="upl__queue__status upl__queue__status--review">
        {zip.ingested} in review
      </span>
      <span className="upl__queue__zipmeta">
        {zip.duplicates > 0 ? `${zip.duplicates} dup · ` : ""}
        {skipped > 0 ? `${skipped} skipped · ` : ""}
        {failed > 0 ? `${failed} failed · ` : ""}
        {zip.totalEntries} file{zip.totalEntries === 1 ? "" : "s"}
      </span>
      <Link className="upl__queue__cir" to="/upload?tab=all">
        View all uploads →
      </Link>
    </span>
  );
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  if (i < 0) return "FILE";
  return name.slice(i + 1).toUpperCase().slice(0, 4);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function pagesGuess(f: File): string {
  // Rough estimate — 1 page ≈ 60 KB for DOCX, ~40 KB for PDF — purely
  // visual filler matching the wireframe's "42 pp" style. Real page
  // counts arrive from Stage-1 OCR.
  const kb = f.size / 1024;
  const pp = Math.max(1, Math.round(kb / 50));
  return `${pp} pp`;
}

// Drop-zone source glyphs (sized by `.upl__src svg`).
function FileIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function FolderIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  );
}
